import { CommonModule } from '@angular/common'
import { Component, ElementRef, HostListener, ViewChild, computed, inject, signal } from '@angular/core'
import { FormsModule } from '@angular/forms'
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome'
import { OptimUiSharedModule } from '../../shared/optimus-ui-shared.module'
import { ClassManagerApiService } from '../../_services/system_api/classManager/api.service'
import { FuseqSessionService } from '../../FuseQ/session.service'
import { SiteHubComponent } from '../../views/awp/site-hub/site-hub'

interface SiteSearchOption {
  fraz: string
  label: string
  /** Testo normalizzato su cui cercare (frazionario, denominazione, comune, provincia). */
  search: string
}

const MAX_RESULTS = 8

/**
 * Ricerca globale nella top bar: cerca un frazionario e apre la scheda sede
 * (site hub) in dialog, senza cambiare pagina. I dati dell'anagrafica siti
 * vengono caricati alla prima apertura della ricerca, non all'avvio dell'app.
 */
@Component({
  selector: 'app-header-site-search',
  standalone: true,
  imports: [OptimUiSharedModule, CommonModule, FormsModule, FontAwesomeModule, SiteHubComponent],
  templateUrl: './header-site-search.component.html',
  styleUrls: ['./header-site-search.component.scss']
})
export class HeaderSiteSearchComponent {
  private readonly classApi = inject(ClassManagerApiService)
  private readonly fuseqSession = inject(FuseqSessionService)
  private readonly root = inject(ElementRef<HTMLElement>)

  @ViewChild('searchInput') private searchInput?: ElementRef<HTMLInputElement>

  /** Visibile solo con il permesso di monitoraggio code (site hub in anteprima). */
  readonly canSearch = computed(() => {
    const permissions: string[] = this.fuseqSession.session?.permissions ?? []
    return permissions.includes('MENU_QUEUE_MONITORING')
  })

  readonly query = signal('')
  readonly open = signal(false)
  /** Su viewport piccole il campo è sostituito da un'icona e appare in overlay. */
  readonly mobileOpen = signal(false)
  readonly loading = signal(false)
  readonly loadError = signal(false)
  readonly sites = signal<SiteSearchOption[]>([])
  readonly activeIndex = signal(0)
  readonly dialogVisible = signal(false)
  readonly selectedFraz = signal('')

  private loaded = false

  readonly results = computed(() => {
    const term = normalize(this.query())
    const sites = this.sites()
    if (!term) {
      return sites.slice(0, MAX_RESULTS)
    }
    return sites
      .map((site) => ({ site, score: scoreSite(site, term) }))
      .filter((entry) => entry.score > 0)
      .sort((left, right) => right.score - left.score || left.site.fraz.localeCompare(right.site.fraz))
      .slice(0, MAX_RESULTS)
      .map((entry) => entry.site)
  })

  onFocus (): void {
    this.open.set(true)
    this.ensureLoaded()
  }

  /** Apre la ricerca in overlay sulle viewport piccole e porta il focus sull'input. */
  openMobileSearch (): void {
    this.mobileOpen.set(true)
    this.open.set(true)
    this.ensureLoaded()
    setTimeout(() => this.searchInput?.nativeElement.focus())
  }

  closeMobileSearch (): void {
    this.mobileOpen.set(false)
    this.open.set(false)
  }

  onQueryChange (value: string): void {
    this.query.set(String(value ?? ''))
    this.activeIndex.set(0)
    this.open.set(true)
    this.ensureLoaded()
  }

  onKeydown (event: KeyboardEvent): void {
    const results = this.results()
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault()
        if (!this.open()) {
          this.open.set(true)
          this.ensureLoaded()
          return
        }
        if (results.length > 0) {
          this.activeIndex.update((index) => (index + 1) % results.length)
        }
        return
      case 'ArrowUp':
        event.preventDefault()
        if (results.length > 0) {
          this.activeIndex.update((index) => (index - 1 + results.length) % results.length)
        }
        return
      case 'Enter': {
        const site = results[this.activeIndex()] ?? results[0]
        if (site) {
          event.preventDefault()
          this.openSite(site)
        }
        return
      }
      case 'Escape':
        this.closeMobileSearch()
        return
    }
  }

  openSite (site: SiteSearchOption): void {
    this.query.set(site.fraz)
    this.selectedFraz.set(site.fraz)
    this.open.set(false)
    this.mobileOpen.set(false)
    this.dialogVisible.set(true)
  }

  clear (): void {
    this.query.set('')
    this.activeIndex.set(0)
    this.open.set(true)
    this.ensureLoaded()
    this.searchInput?.nativeElement.focus()
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick (event: MouseEvent): void {
    if (!this.root.nativeElement.contains(event.target as Node)) {
      this.open.set(false)
      this.mobileOpen.set(false)
    }
  }

  private ensureLoaded (): void {
    if (this.loaded || this.loading()) {
      return
    }
    this.loading.set(true)
    this.loadError.set(false)
    this.classApi.getAnagraficaSiti().subscribe({
      next: (rows) => {
        const options = (Array.isArray(rows) ? rows : [])
          .filter((row) => row && row.fraz)
          .map((row) => {
            const fraz = String(row.fraz).trim()
            const label = [row.denominazione, row.comune].filter((value) => Boolean(value)).join(' · ')
            return {
              fraz,
              label,
              search: normalize([fraz, row.denominazione, row.comune, row.sigla_provincia].filter(Boolean).join(' '))
            }
          })
          .sort((left, right) => left.fraz.localeCompare(right.fraz))
        this.sites.set(options)
        this.loading.set(false)
        this.loaded = true
      },
      error: () => {
        this.loading.set(false)
        this.loadError.set(true)
      }
    })
  }
}

/** Minuscole e senza accenti, per una ricerca tollerante. */
function normalize (value: string | null | undefined): string {
  return String(value ?? '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim()
}

/** Punteggio di corrispondenza: frazionario esatto > prefisso > contiene > altri campi. */
function scoreSite (site: SiteSearchOption, term: string): number {
  const fraz = normalize(site.fraz)
  if (fraz === term) {
    return 4
  }
  if (fraz.startsWith(term)) {
    return 3
  }
  if (fraz.includes(term)) {
    return 2
  }
  if (site.search.includes(term)) {
    return 1
  }
  return 0
}
